import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  TrendingDown, Repeat, Heart, Sun, Trophy, Clock, Sparkles, X, ArrowRight,
} from "lucide-react";
import { cn } from "@/lib/utils";

const ICON_MAP = {
  trending_down: TrendingDown,
  repeat: Repeat,
  heart: Heart,
  sun: Sun,
  trophy: Trophy,
  clock: Clock,
  sparkles: Sparkles,
};

const TYPE_STYLES = {
  concern: {
    bg: "bg-amber-50 dark:bg-amber-950/20",
    border: "border-amber-200/60 dark:border-amber-800/40",
    icon: "text-amber-600 dark:text-amber-400",
    iconBg: "bg-amber-100 dark:bg-amber-900/40",
  },
  pattern: {
    bg: "bg-violet-50 dark:bg-violet-950/20",
    border: "border-violet-200/60 dark:border-violet-800/40",
    icon: "text-violet-600 dark:text-violet-400",
    iconBg: "bg-violet-100 dark:bg-violet-900/40",
  },
  support: {
    bg: "bg-rose-50 dark:bg-rose-950/20",
    border: "border-rose-200/60 dark:border-rose-800/40",
    icon: "text-rose-600 dark:text-rose-400",
    iconBg: "bg-rose-100 dark:bg-rose-900/40",
  },
  celebrate: {
    bg: "bg-emerald-50 dark:bg-emerald-950/20",
    border: "border-emerald-200/60 dark:border-emerald-800/40",
    icon: "text-emerald-600 dark:text-emerald-400",
    iconBg: "bg-emerald-100 dark:bg-emerald-900/40",
  },
  remind: {
    bg: "bg-sky-50 dark:bg-sky-950/20",
    border: "border-sky-200/60 dark:border-sky-800/40",
    icon: "text-sky-600 dark:text-sky-400",
    iconBg: "bg-sky-100 dark:bg-sky-900/40",
  },
  encourage: {
    bg: "bg-teal-50 dark:bg-teal-950/20",
    border: "border-teal-200/60 dark:border-teal-800/40",
    icon: "text-teal-600 dark:text-teal-400",
    iconBg: "bg-teal-100 dark:bg-teal-900/40",
  },
};

export function InsightNotifications() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [toastShown, setToastShown] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) return;
    const fetch = async () => {
      try {
        const { data } = await axios.get(`${API}/insights/mood-notifications`, { headers });
        const notifs = data.notifications || [];
        setNotifications(notifs);
        setLoaded(true);

        // Show toast on login for important notifications
        if (!toastShown && notifs.length > 0) {
          const top = notifs[0];
          const style = TYPE_STYLES[top.type] || TYPE_STYLES.encourage;
          if (["concern", "pattern", "support"].includes(top.type)) {
            toast(top.title, {
              description: top.message.slice(0, 100) + (top.message.length > 100 ? "..." : ""),
              duration: 6000,
              action: top.action ? {
                label: top.action,
                onClick: () => top.action_route && navigate(top.action_route),
              } : undefined,
            });
          }
          setToastShown(true);
        }
      } catch (err) {
        setLoaded(true);
      }
    };
    fetch();
  }, [token]);

  const dismiss = async (notifId) => {
    setNotifications(prev => prev.filter(n => n.id !== notifId));
    try {
      await axios.post(`${API}/insights/dismiss-notification`, { notification_id: notifId }, { headers });
    } catch {}
  };

  if (!loaded || notifications.length === 0) return null;

  return (
    <div data-testid="insight-notifications" className="space-y-2 px-1">
      {notifications.map((notif, i) => {
        const style = TYPE_STYLES[notif.type] || TYPE_STYLES.encourage;
        const IconComp = ICON_MAP[notif.icon] || Sparkles;
        return (
          <div
            key={notif.id}
            data-testid={`insight-notification-${notif.id}`}
            className={cn(
              "relative p-3 rounded-2xl border transition-all",
              "animate-in fade-in slide-in-from-bottom-2",
              style.bg, style.border,
            )}
            style={{ animationDelay: `${i * 100}ms`, animationFillMode: "both" }}
          >
            <button
              data-testid={`dismiss-notification-${notif.id}`}
              onClick={() => dismiss(notif.id)}
              className="absolute top-2 right-2 p-1 rounded-full text-muted-foreground/50 hover:text-muted-foreground hover:bg-black/5 dark:hover:bg-white/5 transition-all"
            >
              <X className="w-3 h-3" />
            </button>
            <div className="flex gap-3">
              <div className={cn("w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0", style.iconBg)}>
                <IconComp className={cn("w-4 h-4", style.icon)} strokeWidth={2} />
              </div>
              <div className="flex-1 min-w-0 pr-4">
                <p className="text-sm font-semibold mb-0.5">{notif.title}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">{notif.message}</p>
                {notif.action && (
                  <button
                    data-testid={`notification-action-${notif.id}`}
                    onClick={() => notif.action_route && navigate(notif.action_route)}
                    className={cn("mt-2 inline-flex items-center gap-1 text-xs font-medium transition-colors", style.icon)}
                  >
                    {notif.action} <ArrowRight className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
